import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle, ArrowCounterClockwise, Trash, Envelope } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function MaintenanceReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance");
      setReports(data.reports || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/maintenance/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/maintenance/${id}`);
      toast.success("Report deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openCount = reports.filter((r) => r.status !== "resolved").length;

  return (
    <div className="max-w-3xl" data-testid="maintenance-panel">
      <span className="subhead text-accent text-sm">Guest Reports</span>
      <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Maintenance Reports</h2>
      <p className="text-muted-foreground mt-3 text-sm max-w-lg flex items-center gap-2">
        <Envelope size={16} className="text-accent" />
        New reports are emailed to troy@schellmi.com and listed here.
      </p>

      {!loading && reports.length > 0 && (
        <p className="mt-6 subhead text-foreground/50 text-xs">
          {openCount} open · {reports.length} total
        </p>
      )}

      <div className="mt-4 space-y-4" data-testid="reports-list">
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : reports.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center" data-testid="reports-empty">
            <p className="font-display text-2xl text-foreground/70">No reports yet</p>
            <p className="text-muted-foreground text-sm mt-2">Guest maintenance reports will appear here.</p>
          </div>
        ) : (
          reports.map((r) => (
            <div
              key={r.id}
              data-testid={`report-${r.id}`}
              className={"border border-border p-5 bg-background " + (r.status === "resolved" ? "opacity-60" : "")}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-xl text-foreground">
                    {r.area || "General"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {r.guest_name ? `${r.guest_name} · ` : ""}{formatDate(r.created_at)}
                    {r.status === "resolved" ? " · Resolved" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => toggle(r.id)}
                    data-testid={`toggle-report-${r.id}`}
                    title={r.status === "resolved" ? "Mark as open" : "Mark as resolved"}
                    className={"transition-colors " + (r.status === "resolved" ? "text-muted-foreground hover:text-accent" : "text-accent hover:opacity-70")}
                  >
                    {r.status === "resolved" ? <ArrowCounterClockwise size={18} /> : <CheckCircle size={20} weight="duotone" />}
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    data-testid={`delete-report-${r.id}`}
                    className="text-destructive/70 hover:text-destructive transition-colors"
                  >
                    <Trash size={17} />
                  </button>
                </div>
              </div>
              <p className="text-[15px] leading-relaxed text-foreground/80 mt-3 whitespace-pre-wrap">{r.message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
